import { Component, OnInit } from '@angular/core';
import { Store } from '@ngxs/store';
import { Roles } from 'src/app/common/enumerations/enumerations';
import { UserRole } from 'src/app/services/user-roles/user-role';
import { AppState } from 'src/app/store/reducer';
import { TransactionLogService } from 'src/app/services/logs/transaction-log.service';
import { TransactionLog } from 'src/app/services/logs/transaction-logs';
import { ApplicationLogContent, ApplicationLogDate, ApplicationLogsService } from './application-logs.service';

interface ApplicationLogLine {
  timestamp?: string;
  text: string;
  levelClass: string;
}

type ApplicationLogType = 'system' | 'transaction';

@Component({
  selector: 'app-application-logs',
  templateUrl: './application-logs.component.html',
  styleUrls: ['./application-logs.component.scss']
})
export class ApplicationLogsComponent implements OnInit {
  dates: ApplicationLogDate[] = [];
  transactionLogs: TransactionLog[] = [];
  selectedLogType: ApplicationLogType = 'system';
  selectedDate?: string;
  log?: ApplicationLogContent;
  logLines: ApplicationLogLine[] = [];
  currentPage = 1;
  readonly logLevels = ['Verbose', 'Debug', 'Information', 'Warning', 'Error', 'Fatal'];
  selectedLogLevel?: string;
  canManageLogLevel = false;
  levelUpdating = false;
  levelError?: string;
  loading = false;
  error?: string;

  constructor(private applicationLogsService: ApplicationLogsService, private transactionLogService: TransactionLogService, private store: Store) { }

  /** Loads available dates and opens the newest log automatically. */
  ngOnInit(): void {
    // Avoid the SA-only endpoint for ADM users because the global interceptor treats a 403 as an expired session.
    const currentUser = this.store.selectSnapshot(AppState).currentUser;
    this.canManageLogLevel = currentUser?.userRole?.some((role: UserRole) => role.roleID == Roles.SA) == true;
    // Let the protected API decide access so the current level remains visible even when role data is delayed.
    this.loadLogLevel();
    this.loadDates();
  }

  /** Switches between system log files and transaction-log records. */
  selectLogType(logType: ApplicationLogType): void {
    this.selectedLogType = logType;
    this.error = undefined;
    this.loading = false;

    this.loadDates();
  }

  /** Loads transaction-log records for the selected date. */
  private loadTransactionLogs(date: string): void {
    this.loading = true;
    this.transactionLogService.GetByDate(date).subscribe({
      next: logs => {
        this.transactionLogs = logs ?? [];
        this.loading = false;
      },
      error: () => {
        this.error = 'Transaction logs could not be loaded.';
        this.loading = false;
      }
    });
  }

  /** Maps a transaction result to the terminal color used by the log row. */
  getTransactionLogClass(transactionLog: TransactionLog): string {
    const code = (transactionLog.code ?? '').toUpperCase();
    const name = (transactionLog.name ?? '').toUpperCase();

    if (code == 'ERROR' || name.includes('ERROR') || name.includes('FAIL')) return 'transaction-log-error';
    if (code == 'WARNING' || code == 'WARN' || name.includes('WARN')) return 'transaction-log-warning';
    return 'transaction-log-success';
  }

  /** Reloads retained dates and refreshes the selected log without changing its page. */
  refreshLog(): void {
    if (this.selectedLogType == 'transaction') this.loadDates(true);
    else this.loadDates(true);
  }

  /**
   * Loads retained log dates and selects or refreshes the appropriate date.
   * @param preserveSelection Keeps the current date and page when they remain available.
   */
  private loadDates(preserveSelection = false): void {
    this.loading = true;
    this.error = undefined;
    const datesRequest = this.selectedLogType == 'transaction'
      ? this.transactionLogService.GetDates()
      : this.applicationLogsService.getDates();
    datesRequest.subscribe({
      next: dates => {
        // Keep the Administration selector focused on the most recent six months of logs.
        const oldestAllowedDate = new Date();
        oldestAllowedDate.setHours(0, 0, 0, 0);
        oldestAllowedDate.setMonth(oldestAllowedDate.getMonth() - 6);
        this.dates = dates.filter(item => new Date(`${item.date}T00:00:00`) >= oldestAllowedDate);
        const selectedDateAvailable = preserveSelection && this.dates.some(item => item.date == this.selectedDate);
        if (selectedDateAvailable) {
          if (this.selectedLogType == 'transaction' && this.selectedDate) this.loadTransactionLogs(this.selectedDate);
          else this.loadPage();
        }
        else if (this.dates.length) this.selectDate(this.dates[0].date);
        else this.loading = false;
      },
      error: () => {
        this.error = this.selectedLogType == 'transaction'
          ? 'Transaction log dates could not be loaded.'
          : 'Application logs could not be loaded.';
        this.loading = false;
      }
    });
  }

  /** Loads the current level and reveals the selector only when the caller is authorized. */
  private loadLogLevel(): void {
    this.applicationLogsService.getLevel().subscribe({
      next: resource => {
        this.selectedLogLevel = resource.level;
        this.canManageLogLevel = true;
      },
      // ADM can view logs but the level selector is intentionally restricted to SA.
      error: () => this.canManageLogLevel = false
    });
  }

  /**
   * Applies and persists a new Serilog minimum level.
   * @param level Selected Serilog event level name.
   */
  changeLogLevel(level: string): void {
    const previousLevel = this.selectedLogLevel;
    this.levelUpdating = true;
    this.levelError = undefined;
    this.applicationLogsService.updateLevel(level).subscribe({
      next: resource => {
        this.selectedLogLevel = resource.level;
        this.levelUpdating = false;
      },
      error: () => {
        this.selectedLogLevel = previousLevel;
        this.levelError = 'The application log level could not be updated.';
        this.levelUpdating = false;
      }
    });
  }

  /**
   * Loads log content for the selected retained date.
   * @param date Date formatted as yyyy-MM-dd.
   */
  selectDate(date: string): void {
    this.selectedDate = date;
    this.currentPage = 1;
    if (this.selectedLogType == 'transaction') this.loadTransactionLogs(date);
    else this.loadPage();
  }

  /** Loads the numbered log page for the currently selected date. */
  private loadPage(): void {
    if (!this.selectedDate) return;

    this.loading = true;
    this.error = undefined;
    this.log = undefined;
    this.logLines = [];
    this.applicationLogsService.getByDate(this.selectedDate, this.currentPage).subscribe({
      next: log => {
        this.log = log;
        this.currentPage = log.page;
        // Classify each line once so large logs do not invoke template methods during change detection.
        this.logLines = this.colorizeLines(log.content);
        this.loading = false;
      },
      error: () => {
        this.error = 'The selected log could not be loaded.';
        this.loading = false;
      }
    });
  }

  /** Opens the preceding, older page of the selected log date. */
  showOlder(): void {
    if (!this.log || this.currentPage >= this.log.totalPages) return;
    this.currentPage++;
    this.loadPage();
  }

  /** Returns to the newer page most recently visited. */
  showNewer(): void {
    if (this.currentPage <= 1) return;
    this.currentPage--;
    this.loadPage();
  }

  /** Opens the oldest, first chronological page of the selected log date. */
  showFirst(): void {
    if (!this.log || this.currentPage == this.log.totalPages) return;
    this.currentPage = this.log.totalPages;
    this.loadPage();
  }

  /** Opens the newest, last chronological page of the selected log date. */
  showLast(): void {
    if (this.currentPage == 1) return;
    this.currentPage = 1;
    this.loadPage();
  }

  /**
   * Maps Serilog level tokens to display colors while preserving the original text.
   * @param content Combined Serilog content returned by the API.
   * @returns Lines with their corresponding display class.
   */
  private colorizeLines(content: string): ApplicationLogLine[] {
    let currentLevelClass = 'log-default';

    return content.split(/\r?\n/).map(text => {
      // Continuation lines inherit the current entry's level so exception stacks remain one color.
      if (/^=====/.test(text)) currentLevelClass = 'log-default';
      else if (/^\d{4}-\d{2}-\d{2}.*\[(ERR|FTL)\]/.test(text)) currentLevelClass = 'log-error';
      else if (/^\d{4}-\d{2}-\d{2}.*\[WRN\]/.test(text)) currentLevelClass = 'log-warning';
      else if (/^\d{4}-\d{2}-\d{2}.*\[INF\]/.test(text)) currentLevelClass = 'log-success';
      else if (/^\d{4}-\d{2}-\d{2}.*\[[A-Z]{3}\]/.test(text)) currentLevelClass = 'log-default';

      const timestampMatch = text.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}\.\d{3} [+-]\d{2}:\d{2}/);
      return {
        timestamp: timestampMatch?.[0],
        text: timestampMatch ? text.substring(timestampMatch[0].length).replace(/^\s+/, '') : text,
        levelClass: currentLevelClass
      };
    });
  }
}
